/* eslint-disable @typescript-eslint/no-explicit-any */
// Presentational leaf views for the A2UI report catalog. Props-only, no
// store or app-logic access. The binder may deliver partial snapshots while
// a surface is still streaming, so every view defaults missing props to
// empty values and renders nothing when there is no data.

import type { BadgeVariantType } from "@/components/ui/badge";
import { Badge } from "@/components/ui/badge";
import DonutChart from "@/components/ui/chart/DonutChart";
import HorizontalBarChart from "@/components/ui/chart/HorizontalBarChart";
import LineChart from "@/components/ui/chart/LineChart";
import VerticalBarChart from "@/components/ui/chart/VerticalBarChart";
import { Markdown } from "@/components/ui/markdown";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { formatMeasure } from "@/utils/format";

// Cell and card values come from the A2UI data model, which the protocol never
// validates against the catalog, so a binding object can arrive where a number
// is declared. Numbers take the shared report formatting; anything that is not
// a primitive renders as nothing instead of throwing "Objects are not valid as
// a React child" and taking the whole route down with it.
function cellText(value: unknown): string {
  if (typeof value === "number") {
    return Number.isFinite(value) ? formatMeasure(value, "chart") : "";
  }
  return typeof value === "string" ? value : "";
}

// ── ReportText ──────────────────────────────────────────────────────────

interface ReportTextViewProps {
  markdown?: string;
  replace?: any;
}

export function ReportTextView({
  markdown = "",
  replace
}: ReportTextViewProps) {
  if (!markdown) return null;
  return (
    <Markdown className="markdown-a2ui" content={markdown} replace={replace} />
  );
}

// ── ReportChart ─────────────────────────────────────────────────────────

// Bars are capped rather than left to fill the category band: a band is as
// wide as the container allows, and a maximised panel turns a five-bar chart
// into five slabs with a 999px corner radius. Below the cap the bars still
// fill the band, so the narrow panel looks as it did.
const SINGLE_BAR_WIDTH = 64;
const GROUPED_BAR_WIDTH = 32;

// One rounding for every chart in a report. The kit otherwise rounds a
// single-series bar into a full cap and a grouped one by 10px, so two charts
// side by side disagree for no reason the reader can see.
const BAR_CORNER_RADIUS = 6;

// A chart 360px tall and 1900px wide reads as a strip, whatever the bars do.
// The cap only bites once the panel is maximised, and the chart sits centred
// rather than hugging the left edge of a wide panel.
function ChartFrame({ children }: { children: React.ReactNode }) {
  return (
    <div data-print-avoid-break className="mx-auto w-full max-w-3xl">
      {children}
    </div>
  );
}

export interface ReportChartSeries {
  dataKey: string;
  name: string;
  color: string;
}

interface ReportChartViewProps {
  title?: string;
  baseChart?: "bar" | "line" | "pie";
  orientation?: "vertical" | "horizontal";
  xAxisKey?: string;
  series?: ReportChartSeries[];
  data?: Record<string, string | number>[];
  onBarClick?: (name: string) => void;
  isPrint?: boolean;
}

export function ReportChartView({
  title = "",
  baseChart = "bar",
  orientation = "vertical",
  xAxisKey,
  series = [],
  data = [],
  onBarClick,
  isPrint
}: ReportChartViewProps) {
  const kitSeries = series.map((item) => ({
    dataKey: item.dataKey,
    label: item.name,
    color: item.color
  }));

  // Pie rows map to { name, value } pairs: xAxisKey is the slice name and the
  // first series is the value source.
  if (baseChart === "pie") {
    const first = series[0];
    const donutData = data.map((row) => ({
      name: xAxisKey ? String(row[xAxisKey] ?? "") : "",
      value: first ? Number(row[first.dataKey]) : 0
    }));

    return (
      <ChartFrame>
        {title && (
          <h3 className="text-sm font-semibold text-txtBody">{title}</h3>
        )}
        <DonutChart data={donutData} height={360} />
      </ChartFrame>
    );
  }

  const common = {
    data,
    series: kitSeries,
    categoryKey: xAxisKey,
    title: title || undefined
  };

  if (baseChart === "line") {
    return (
      <ChartFrame>
        <LineChart {...common} height={360} isPrint={isPrint} />
      </ChartFrame>
    );
  }
  if (orientation === "horizontal") {
    return (
      <ChartFrame>
        <HorizontalBarChart
          data={data}
          series={kitSeries}
          categoryKey={xAxisKey}
          title={title || undefined}
          onBarClick={onBarClick}
          cornerRadius={BAR_CORNER_RADIUS}
          height={360}
          isPrint={isPrint}
        />
      </ChartFrame>
    );
  }
  return (
    <ChartFrame>
      <VerticalBarChart
        data={data}
        series={kitSeries}
        categoryKey={xAxisKey}
        title={title || undefined}
        onBarClick={onBarClick}
        maxBarSize={kitSeries.length > 1 ? GROUPED_BAR_WIDTH : SINGLE_BAR_WIDTH}
        cornerRadius={BAR_CORNER_RADIUS}
        height={360}
        isPrint={isPrint}
      />
    </ChartFrame>
  );
}

// ── ReportMetricCards ───────────────────────────────────────────────────

export interface ReportMetricCard {
  // Typed as unknown on purpose: the binder hands these straight from the data
  // model, so the catalog's declared types are a hope, not a guarantee.
  label?: unknown;
  value?: unknown;
  caption?: unknown;
}

interface ReportMetricCardsViewProps {
  cards?: ReportMetricCard[];
}

export function ReportMetricCardsView({
  cards = []
}: ReportMetricCardsViewProps) {
  if (!cards.length) return null;

  return (
    // A 220px floor is what decides the wrap: the report panel fits two
    // columns, so four cards read as 2x2 instead of three and an orphan, while
    // a maximised panel still lays them out in a single row.
    <div
      data-print-avoid-break
      className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]"
    >
      {cards.map((card, index) => (
        <div
          key={cellText(card.label) || index}
          className="rounded-lg border border-gainsboro bg-white px-3.5 py-3"
        >
          <p className="text-2xl font-bold leading-tight text-primary-teal [font-variant-numeric:tabular-nums]">
            {cellText(card.value)}
          </p>
          <p className="mt-0.5 text-xs font-bold text-txtHeading">
            {cellText(card.label)}
          </p>
          <p className="text-[11px] leading-tight text-txtBody">
            {cellText(card.caption)}
          </p>
        </div>
      ))}
    </div>
  );
}

// ── ReportTableGrid ─────────────────────────────────────────────────────

export interface ReportGridColumn {
  key: string;
  label: string;
  align: "left" | "right";
  render?: "badge" | "accent";
  tones?: Record<string, BadgeVariantType>;
}

// A badge column only colours the values its tone map names. Anything else —
// an unmapped rating, a plain column — stays raw text, so an unexpected value
// still reads instead of disappearing.
function renderCell(column: ReportGridColumn, value: unknown) {
  const text = cellText(value);
  if (column.render === "accent") {
    return <span className="font-semibold text-primary-teal">{text}</span>;
  }
  // The tone map is keyed on the raw value the model wrote, not the formatted
  // text, so grouping separators never break a lookup.
  const tone =
    column.render === "badge" ? column.tones?.[String(value)] : undefined;
  if (!tone) return text;
  return <Badge variant={tone}>{text}</Badge>;
}

interface ReportTableGridViewProps {
  columns?: ReportGridColumn[];
  rows?: Record<string, unknown>[];
}

export function ReportTableGridView({
  columns = [],
  rows = []
}: ReportTableGridViewProps) {
  if (!columns.length || !rows.length) return null;

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-gainsboro">
      <Table className="w-full text-xs">
        <TableHeader>
          <TableRow className="border-gainsboro">
            {columns.map((column) => (
              <TableHead
                key={column.key}
                scope="col"
                className={cn(
                  "h-10 bg-white-smoke px-3 font-semibold text-txtHeading",
                  column.align === "right" && "text-right"
                )}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              className="border-gainsboro last:border-b-0"
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={cn(
                    "px-3 py-3 align-top text-txtBody",
                    column.align === "right" && "text-right"
                  )}
                >
                  {renderCell(column, row[column.key])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
